import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS, ROLE_HIERARCHY, type RoleCode } from '@oxxean/types';
import {
  createUser,
  fetchUsers,
  forcePasswordReset,
  setUserActive,
  updateUser,
} from '../../lib/api/admin';
import { queryKeys } from '../../lib/query-client';
import { errorMessage } from '../../lib/errors';
import { formatDateTime } from '../../lib/format';
import { useAuth } from '../../hooks/useAuth';
import { useFilters } from '../../hooks/useFilters';
import { useTableState } from '../../hooks/useTableState';
import { useToasts } from '../../hooks/useToasts';
import type { CatalogOption, UserInput, UserListItem } from '../../types/domain';
import { DataBoundary } from '../../components/DataBoundary';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { Badge, Button, Card, Notice, TableSkeleton } from '../../components/primitives';
import { CheckboxField, Field, TextField } from '../../components/form';
import { PageHeading } from '../layout/PageHeading';

/**
 * Administración de usuarios.
 *
 * Un usuario no puede asignar un rol de nivel igual o superior al propio (§11): la
 * interfaz filtra los roles asignables con `ROLE_HIERARCHY`, y el backend vuelve a
 * comprobarlo.
 */
export function UsersPage(): JSX.Element {
  const { can, user: currentUser } = useAuth();
  const { options } = useFilters();
  const { push } = useToasts();
  const queryClient = useQueryClient();
  const table = useTableState({ sortBy: 'fullName' });

  const [editing, setEditing] = useState<UserListItem | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<UserListItem | null>(null);
  const [pendingReset, setPendingReset] = useState<UserListItem | null>(null);

  const canManage = can(PERMISSIONS.USERS_MANAGE);

  const ownLevel = Math.min(
    ...(currentUser?.roles ?? []).map((role) => ROLE_HIERARCHY[role] ?? Number.MAX_SAFE_INTEGER),
  );
  const assignableRoles = (Object.keys(ROLE_HIERARCHY) as RoleCode[]).filter(
    (role) => ROLE_HIERARCHY[role] > ownLevel,
  );

  const listQuery = useQuery({
    queryKey: queryKeys.users(table.state),
    queryFn: () => fetchUsers(table.state),
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const saveMutation = useMutation({
    mutationFn: async (payload: UserInput) =>
      editing === null ? createUser(payload) : updateUser(editing.id, payload),
    onSuccess: () => {
      push({
        kind: 'success',
        title: editing === null ? 'Usuario creado' : 'Usuario actualizado',
        message:
          editing === null
            ? 'El sistema envía la contraseña provisional al correo indicado; el usuario deberá cambiarla al ingresar.'
            : undefined,
      });
      setFormOpen(false);
      setEditing(null);
      invalidate();
    },
    onError: (error) => {
      push({ kind: 'error', title: 'No se pudo guardar el usuario', message: errorMessage(error) });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => setUserActive(id, active),
    onSuccess: () => {
      push({ kind: 'success', title: 'Estado actualizado' });
      setPendingToggle(null);
      invalidate();
    },
    onError: (error) => {
      push({ kind: 'error', title: 'No se pudo cambiar el estado', message: errorMessage(error) });
    },
  });

  const resetMutation = useMutation({
    mutationFn: (id: string) => forcePasswordReset(id),
    onSuccess: () => {
      push({
        kind: 'success',
        title: 'Restablecimiento forzado',
        message: 'El usuario deberá definir una contraseña nueva en su próximo ingreso.',
      });
      setPendingReset(null);
      invalidate();
    },
    onError: (error) => {
      push({ kind: 'error', title: 'No se pudo forzar el restablecimiento', message: errorMessage(error) });
    },
  });

  const columns = useMemo<Array<Column<UserListItem>>>(
    () => [
      {
        key: 'fullName',
        header: 'Nombre',
        sortable: true,
        render: (row) => row.fullName,
        plain: (row) => row.fullName,
      },
      {
        key: 'email',
        header: 'Correo',
        sortable: true,
        render: (row) => row.email,
        plain: (row) => row.email,
      },
      {
        key: 'roles',
        header: 'Roles',
        render: (row) => row.roles.join(', '),
        plain: (row) => row.roles.join(', '),
      },
      {
        key: 'companyScope',
        header: 'Ámbito de empresa',
        render: (row) =>
          row.companyScope.length === 0
            ? 'Todas'
            : row.companyScope.map((scope) => scope.label).join(', '),
        plain: (row) => row.companyScope.map((scope) => scope.label).join(', '),
      },
      {
        key: 'areaScope',
        header: 'Ámbito de área',
        render: (row) =>
          row.areaScope.length === 0 ? 'Todas' : row.areaScope.map((scope) => scope.label).join(', '),
        plain: (row) => row.areaScope.map((scope) => scope.label).join(', '),
      },
      {
        key: 'active',
        header: 'Estado',
        sortable: true,
        render: (row) => (
          <>
            <Badge tone={row.active ? 'green' : 'neutral'}>{row.active ? 'Activo' : 'Inactivo'}</Badge>
            {row.mustChangePassword && (
              <>
                {' '}
                <Badge tone="amber">Debe cambiar clave</Badge>
              </>
            )}
          </>
        ),
        plain: (row) => (row.active ? 'Activo' : 'Inactivo'),
      },
      {
        key: 'lastLoginAt',
        header: 'Último acceso',
        sortable: true,
        render: (row) => formatDateTime(row.lastLoginAt),
        plain: (row) => row.lastLoginAt,
      },
    ],
    [],
  );

  const rows = listQuery.data;

  return (
    <>
      <PageHeading
        title="Usuarios"
        description="Cuentas de acceso, roles asignados y ámbito de empresa y área. El ámbito no filtra después de consultar: se inyecta en la consulta."
        actions={
          canManage ? (
            <Button
              variant="primary"
              icon="plus"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              Nuevo usuario
            </Button>
          ) : undefined
        }
      />

      {canManage && assignableRoles.length === 0 && (
        <Notice tone="neutral">
          Su rol no permite asignar ningún rol por debajo del propio, por lo que no puede crear
          usuarios nuevos.
        </Notice>
      )}

      <Card title="Cuentas registradas" flush>
        <DataBoundary
          isLoading={listQuery.isLoading}
          isError={listQuery.isError}
          error={listQuery.error}
          onRetry={() => void listQuery.refetch()}
          isEmpty={rows !== undefined && rows.total === 0 && table.state.search.length === 0}
          empty={{ title: 'Sin usuarios', message: 'No hay cuentas registradas.' }}
          skeleton={<TableSkeleton columns={7} />}
        >
          {rows !== undefined && (
            <DataTable
              caption="Usuarios de la plataforma"
              columns={columns}
              rows={rows.data}
              getRowId={(row) => row.id}
              state={table.state}
              onStateChange={table.setState}
              total={rows.total}
              totalPages={rows.totalPages}
              searchPlaceholder="Buscar por nombre o correo…"
              isFetching={listQuery.isFetching}
              rowActions={
                canManage
                  ? (row) => (
                      <>
                        <Button
                          size="sm"
                          variant="ghost"
                          icon="edit"
                          onClick={() => {
                            setEditing(row);
                            setFormOpen(true);
                          }}
                        >
                          Editar
                        </Button>
                        <Button size="sm" variant="ghost" icon="key" onClick={() => setPendingReset(row)}>
                          Restablecer
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setPendingToggle(row)}>
                          {row.active ? 'Desactivar' : 'Activar'}
                        </Button>
                      </>
                    )
                  : undefined
              }
            />
          )}
        </DataBoundary>
      </Card>

      {formOpen && (
        <UserForm
          editing={editing}
          assignableRoles={assignableRoles}
          companies={options?.companies ?? []}
          areasByCompany={options?.areasByCompany ?? []}
          saving={saveMutation.isPending}
          onCancel={() => {
            setFormOpen(false);
            setEditing(null);
          }}
          onSubmit={(payload) => saveMutation.mutate(payload)}
        />
      )}

      <ConfirmDialog
        open={pendingToggle !== null}
        title={pendingToggle?.active === true ? 'Desactivar usuario' : 'Activar usuario'}
        destructive={pendingToggle?.active === true}
        confirmLabel={pendingToggle?.active === true ? 'Desactivar' : 'Activar'}
        loading={toggleMutation.isPending}
        message={
          pendingToggle?.active === true
            ? `${pendingToggle.fullName} perderá el acceso de inmediato y sus sesiones activas se cerrarán.`
            : `${pendingToggle?.fullName ?? ''} podrá volver a ingresar a la plataforma.`
        }
        onCancel={() => setPendingToggle(null)}
        onConfirm={() => {
          if (pendingToggle !== null) {
            toggleMutation.mutate({ id: pendingToggle.id, active: !pendingToggle.active });
          }
        }}
      />

      <ConfirmDialog
        open={pendingReset !== null}
        title="Forzar restablecimiento de contraseña"
        confirmLabel="Forzar restablecimiento"
        loading={resetMutation.isPending}
        message={`Se invalidará la contraseña actual de ${pendingReset?.fullName ?? ''} y se le exigirá definir una nueva en su próximo ingreso.`}
        onCancel={() => setPendingReset(null)}
        onConfirm={() => {
          if (pendingReset !== null) resetMutation.mutate(pendingReset.id);
        }}
      />
    </>
  );
}

interface UserFormProps {
  editing: UserListItem | null;
  assignableRoles: RoleCode[];
  companies: CatalogOption[];
  /**
   * Todas las áreas con su `companyId`, NO la lista deduplicada de filtros.
   *
   * El ámbito de área se aplica como `id: { in: areaScope }`: identificadores
   * literales, sin expansión por nombre. Con la lista deduplicada, conceder
   * «Operaciones» otorgaba la Operaciones de UNA empresa arbitraria mientras la
   * pantalla sugería haber concedido las cinco. Aquí se agrupan por empresa para
   * que quien administra vea exactamente qué está concediendo.
   */
  areasByCompany: CatalogOption[];
  saving: boolean;
  onCancel: () => void;
  onSubmit: (payload: UserInput) => void;
}

function UserForm({
  editing,
  assignableRoles,
  companies,
  areasByCompany,
  saving,
  onCancel,
  onSubmit,
}: UserFormProps): JSX.Element {
  const [email, setEmail] = useState(editing?.email ?? '');
  const [fullName, setFullName] = useState(editing?.fullName ?? '');
  const [roles, setRoles] = useState<RoleCode[]>(editing?.roles ?? []);
  const [active, setActive] = useState(editing?.active ?? true);
  const [companyScopeIds, setCompanyScopeIds] = useState<string[]>(
    editing?.companyScope.map((scope) => scope.id) ?? [],
  );
  const [areaScopeIds, setAreaScopeIds] = useState<string[]>(
    editing?.areaScope.map((scope) => scope.id) ?? [],
  );
  const [error, setError] = useState<string | null>(null);

  const toggle = <T extends string>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

  /**
   * Áreas agrupadas por empresa, en el orden del catálogo de empresas.
   *
   * Sin el agrupamiento aparecerían cinco casillas «Operaciones» indistinguibles y
   * quien administra no podría saber cuál marcar.
   */
  const areaGroups = useMemo(
    () =>
      companies
        .map((company) => ({
          company,
          areas: areasByCompany.filter((area) => area.companyId === company.id),
        }))
        .filter((group) => group.areas.length > 0),
    [companies, areasByCompany],
  );

  const submit = (): void => {
    if (fullName.trim().length < 3) {
      setError('Indique el nombre completo.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('El correo no tiene un formato válido.');
      return;
    }
    if (roles.length === 0) {
      setError('Asigne al menos un rol.');
      return;
    }
    setError(null);
    onSubmit({
      email: email.trim(),
      fullName: fullName.trim(),
      roles,
      active,
      companyScopeIds,
      areaScopeIds,
    });
  };

  return (
    <Modal
      open
      size="wide"
      title={editing === null ? 'Nuevo usuario' : `Editar ${editing.fullName}`}
      onClose={onCancel}
      footer={
        <>
          <Button onClick={onCancel} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="primary" loading={saving} onClick={submit}>
            Guardar
          </Button>
        </>
      }
    >
      {error !== null && (
        <Notice tone="error" role="alert">
          {error}
        </Notice>
      )}

      <div className="form-grid">
        <TextField
          label="Nombre completo"
          required
          value={fullName}
          onChange={(event) => setFullName(event.target.value)}
        />
        <TextField
          label="Correo institucional"
          type="email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <Field label="Roles" required full hint="Sólo se listan los roles por debajo del suyo.">
          <div className="inline">
            {assignableRoles.map((role) => (
              <CheckboxField
                key={role}
                label={role}
                checked={roles.includes(role)}
                onChange={() => setRoles((current) => toggle(current, role))}
              />
            ))}
          </div>
        </Field>

        <Field
          label="Ámbito de empresa"
          full
          hint="Sin selección = acceso a todas las empresas autorizadas por el rol."
        >
          <div className="inline">
            {companies.map((company) => (
              <CheckboxField
                key={company.id}
                label={company.label}
                checked={companyScopeIds.includes(company.id)}
                onChange={() => setCompanyScopeIds((current) => toggle(current, company.id))}
              />
            ))}
            {companies.length === 0 && <span className="tiny muted">Catálogo no disponible.</span>}
          </div>
        </Field>

        <Field
          label="Ámbito de área"
          full
          hint="Sin selección = todas las áreas. Cada área pertenece a una empresa: el ámbito concede esa, no las homónimas de las demás."
        >
          <div className="scope-groups">
            {areaGroups.map((group) => (
              <div className="scope-groups__group" key={group.company.id}>
                <span className="tiny muted">{group.company.label}</span>
                <div className="inline">
                  {group.areas.map((area) => (
                    <CheckboxField
                      key={area.id}
                      label={area.label}
                      checked={areaScopeIds.includes(area.id)}
                      onChange={() => setAreaScopeIds((current) => toggle(current, area.id))}
                    />
                  ))}
                </div>
              </div>
            ))}
            {areaGroups.length === 0 && <span className="tiny muted">Catálogo no disponible.</span>}
          </div>
        </Field>

        <Field label="Estado" full>
          <CheckboxField
            label="Cuenta activa"
            checked={active}
            onChange={(event) => setActive(event.target.checked)}
          />
        </Field>
      </div>
    </Modal>
  );
}
